/** Chart.js registration + theme helpers (one place, so every chart looks the same). Colours come only
 * from CSS variables (styles.css, docs/design.md): state colours (good/warn/bad) for states, the data
 * palette (`--d-*`, zones, phases) for data. */
import { useSyncExternalStore } from "react";
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  TimeScale,
  Tooltip,
  type ChartOptions,
  type LegendItem,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { addDays, parseISO } from "date-fns";
import { day, dayYear, isoDay } from "@/lib/format";

/** Screen readers: every chart canvas says what it shows (its series), unless the page labelled it
 * itself. Chart.js gives the canvas role="img" without a name (found by e2e/a11y.spec.ts). */
function labelCanvas(chart: Chart) {
  const c = chart.canvas;
  if (!c || (c.hasAttribute("aria-label") && !c.dataset.autoLabel)) return;
  const names = chart.data.datasets.map((d) => d.label).filter((x): x is string => !!x);
  c.setAttribute("aria-label", names.length ? `Chart: ${names.join(", ")}` : "Chart");
  c.dataset.autoLabel = "1";
}
const a11yLabel = { id: "a11yLabel", afterInit: labelCanvas, afterUpdate: labelCanvas };

Chart.register(
  a11yLabel,
  LineController,
  LineElement,
  PointElement,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
);

export function cssVar(name: string, fallback = "#888"): string {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

export const palette = () => ({
  text: cssVar("--text", "#222"),
  muted: cssVar("--muted", "#777"),
  grid: cssVar("--border", "#ddd"),
  accent: cssVar("--accent", "#2b44c9"),
  good: cssVar("--good", "#186343"),
  warn: cssVar("--warn", "#a8701a"),
  bad: cssVar("--bad", "#b0352e"),
  info: cssVar("--info", "#2a6590"),
  ochre: cssVar("--ochre", "#b8862a"),
  // data palette: never a state colour for data (docs/design.md → Data colours)
  unpaved: cssVar("--d-gravel", "#b8862a"),
  paved: cssVar("--d-paved", "#5b6670"),
  climb: cssVar("--d-climb", "#8a4b2f"),
  hr: cssVar("--d-hr", "#b0352e"),
  power: cssVar("--d-power", "#2a6590"),
  other: cssVar("--d-other", "#2a6590"),
  zLow: cssVar("--z2", "#5e7bd6"),
  zMid: cssVar("--z3", "#d4a94e"),
  zHigh: cssVar("--z5", "#b0352e"),
  sleepDeep: cssVar("--d-sleep-deep", "#3e4f8f"),
  sleepLight: cssVar("--d-sleep-light", "#7f95d1"),
  sleepRem: cssVar("--d-sleep-rem", "#b28ad6"),
  bodyWeight: cssVar("--d-body-weight", "#2b44c9"),
  bodyFat: cssVar("--d-body-fat", "#b0577f"),
  bodyMuscle: cssVar("--d-body-muscle", "#7a5ab0"),
  bodyWater: cssVar("--d-body-water", "#1f8a9e"),
  bodyBone: cssVar("--d-body-bone", "#6f6a60"),
  bodyBmi: cssVar("--d-body-bmi", "#8f7a3a"),
});

/** `#rrggbb` + alpha (0..1) → `#rrggbbaa`; any other colour is returned as is. */
export function alpha(color: string, a: number): string {
  if (!/^#[0-9a-f]{6}$/i.test(color)) return color;
  return `${color}${Math.round(Math.max(0, Math.min(1, a)) * 255)
    .toString(16)
    .padStart(2, "0")}`;
}

// ---------- theme changes: charts read colours when they render, so they re-render when the theme does
function currentTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  const set = document.documentElement.dataset.theme;
  if (set === "light" || set === "dark") return set;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
function subscribeTheme(cb: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  const mo = new MutationObserver(cb);
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
  const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
  mq?.addEventListener?.("change", cb);
  return () => {
    mo.disconnect();
    mq?.removeEventListener?.("change", cb);
  };
}
/** The effective theme; a component that calls it re-renders (and re-reads `palette()`) on a change. */
export function useChartTheme(): "light" | "dark" {
  return useSyncExternalStore(subscribeTheme, currentTheme, () => "light");
}

/** Legend markers: filled circles in the series' colour (a line's stroke, a bar's fill). */
function legendLabels(chart: Chart): LegendItem[] {
  const items = Chart.defaults.plugins.legend.labels.generateLabels(chart);
  return items.map((l) => {
    const ds = l.datasetIndex != null ? chart.data.datasets[l.datasetIndex] : undefined;
    const type = (ds as { type?: string } | undefined)?.type ?? (chart.config as { type?: string }).type;
    const color = type === "line" ? (l.strokeStyle as string) : (l.fillStyle as string);
    return { ...l, fillStyle: color, strokeStyle: color, lineWidth: 0, pointStyle: "circle" };
  });
}

type Opts = ChartOptions<"line" | "bar">;
const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
/** Plain-object deep merge (arrays and functions are replaced, not merged). */
export function deepMerge<T>(base: T, extra: unknown): T {
  if (!isObj(base) || !isObj(extra)) return (extra === undefined ? base : extra) as T;
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(extra)) out[k] = isObj(v) && isObj(out[k]) ? deepMerge(out[k], v) : v;
  return out as T;
}

/** Base options: responsive, compact legend, themed axes, horizontal gridlines only. `extra` is merged
 * deeply, so a chart that sets `scales.x.stacked` keeps the shared tick and grid settings. */
export function baseOptions(extra: object = {}): Opts {
  const p = palette();
  Chart.defaults.color = p.muted;
  Chart.defaults.font.family = getComputedStyle(document.body).fontFamily;
  const base = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "bottom",
        labels: { boxWidth: 8, boxHeight: 8, usePointStyle: true, generateLabels: legendLabels },
      },
      tooltip: { enabled: true },
    },
    scales: {
      x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 8 } },
      y: { grid: { color: p.grid }, ticks: { maxTicksLimit: 6 } },
    },
  };
  return deepMerge(base, extra) as Opts;
}

// ---------- gap rules per chart (RD-31b, owner D-130; docs/design.md → Charts → Gap rules)
/** How one chart treats missing data. `bridgeDays`: a run of fewer than this many missing days between two values
 * is drawn as the straight line between them, dashed and labelled *estimated* (D-128 pattern); 0 = never bridged,
 * the run stays a labelled gap (OD-08). `plan`: the line is a plan or a projection, not data, so it is drawn
 * through any gap and not called estimated. `breakMinutes`: minute charts break the line where samples are this
 * many minutes apart or more (the watch was off). */
export interface GapRule {
  bridgeDays: number;
  plan?: boolean;
  breakMinutes?: number;
}
/** The one table every chart reads (the owner's 13 rules, D-130). A rule the owner changes later is one line here
 * and one row in docs/design.md. The estimate is drawn, never stored. */
export const CHART_GAP_RULES = {
  /** 1 · Health → Body: weight, fat %, muscle, water, bone, BMI (slow values, D-128). */
  body: { bridgeDays: 30 },
  /** 2 · Health → Blood pressure: daily axis, bridge < 14 days (trends only, readings are irregular). */
  bloodPressure: { bridgeDays: 14 },
  /** 3 · Health → HRV and resting HR: never (day-to-day changes are the signal). */
  recovery: { bridgeDays: 0 },
  /** 4 · Health → Body Battery, stress: never (daily values). */
  stressBattery: { bridgeDays: 0 },
  /** 5 · Health → Sleep (bars): no bar, never zero, "no sleep data" label (a bar can't be estimated). */
  sleep: { bridgeDays: 0 },
  /** 6 · Health → Garmin endurance & hill score: bridge < 30 days (Garmin updates them rarely). */
  garminScores: { bridgeDays: 30 },
  /** 7 · Health → Garmin acute/chronic load: never (Garmin's own daily values). */
  garminLoad: { bridgeDays: 0 },
  /** 8 · Progress → CTL/ATL/TSB: computed every day, rest days are real zeros (no gaps to treat). */
  fitness: { bridgeDays: 0 },
  /** 9a · Progress → Target line: a plan, not data, drawn through. */
  fitnessTarget: { bridgeDays: 0, plan: true },
  /** 9b · Progress → Bike fitness: never (a measurement). */
  bikeFitness: { bridgeDays: 0 },
  /** 10 · Progress → Race readiness history: never (the score is not invented; DV-22 fills weight). */
  amrHistory: { bridgeDays: 0 },
  /** 11 · Progress → Race readiness projection: a projection, drawn through, labelled projected. */
  amrProjection: { bridgeDays: 0, plan: true },
  /** 12 · Progress → weekly bars: 0 for a week without rides, "unknown"/no data only when nothing synced (RD-14). */
  weekly: { bridgeDays: 0 },
  /** 13 · Health → all-day heart rate: the line breaks where the watch was off ≥ 10 minutes. */
  heartRateDay: { bridgeDays: 0, breakMinutes: 10 },
} as const satisfies Record<string, GapRule>;
export type ChartId = keyof typeof CHART_GAP_RULES;

/** Applies a chart's rule to one series: `bridgeDays` > 0 fills a run of fewer than that many missing points with
 * the straight line between the values around it (OD-01: "estimate the average for the period between") and marks
 * them in `estimated`, so the chart can dash them and say *estimated*; a `plan` line is drawn through any run (not
 * estimated); otherwise, and for longer runs and the edges before the first and after the last value, the values
 * stay null. The input is never changed. */
export function applyGapRule(
  values: (number | null | undefined)[],
  rule: GapRule,
): { values: (number | null)[]; estimated: boolean[] } {
  const src = values.map((v) => (v == null || Number.isNaN(v) ? null : v));
  if (rule.plan) return { values: bridgeGaps(src, Infinity).values, estimated: src.map(() => false) };
  return bridgeGaps(src, rule.bridgeDays);
}

/** Fills a run of fewer than `gapDays` missing values with the straight line between the values around it (rounded
 * to 0.01); `estimated[i]` marks the filled points. `gapDays` 0 (or less) never fills. */
export function bridgeGaps(
  values: (number | null)[],
  gapDays: number,
): { values: (number | null)[]; estimated: boolean[] } {
  const out = values.slice();
  const estimated = values.map(() => false);
  let prev = -1;
  values.forEach((v, i) => {
    if (v == null) return;
    const missing = i - prev - 1;
    if (prev >= 0 && missing > 0 && missing < gapDays) {
      const a = values[prev] as number;
      for (let k = prev + 1; k < i; k++) {
        out[k] = Math.round((a + ((v - a) * (k - prev)) / (i - prev)) * 100) / 100;
        estimated[k] = true;
      }
    }
    prev = i;
  });
  return { values: out, estimated };
}

/** Chart.js `segment` option: dashed where either end of a segment is an estimated point. */
export function dashEstimated(estimated: boolean[]) {
  return {
    borderDash: (ctx: { p0DataIndex: number; p1DataIndex: number }) =>
      estimated[ctx.p0DataIndex] || estimated[ctx.p1DataIndex] ? [5, 4] : undefined,
  };
}

/** Minute samples `[minute, value]` with a null point inserted wherever `breakMinutes` or more minutes in a row have
 * no sample, so the line breaks there (not worn is not a low value). Also returns the pauses (first and last minute
 * without a sample). Without `breakMinutes` the samples are returned as they are. */
export function breakPauses(
  samples: [number, number][],
  breakMinutes: number | undefined,
): { points: { minute: number; value: number | null }[]; pauses: { from: number; to: number }[] } {
  const points: { minute: number; value: number | null }[] = [];
  const pauses: { from: number; to: number }[] = [];
  samples.forEach(([m, v], i) => {
    const prev = samples[i - 1];
    if (breakMinutes && prev && m - prev[0] - 1 >= breakMinutes) {
      points.push({ minute: prev[0] + 1, value: null });
      pauses.push({ from: prev[0] + 1, to: m - 1 });
    }
    points.push({ minute: m, value: v });
  });
  return { points, pauses };
}

// ---------- gaps that are not bridged are labelled (RD-31, OD-08)
export interface GapRun {
  from: string;
  to: string;
  /** Number of points (days, weeks) without a value in the run. */
  count: number;
  /** Index of the run's first point in the series. */
  index: number;
  /** True when the run lasts to the end of the series (no value since `from`). */
  trailing: boolean;
}

/** The runs of missing values (null/undefined/NaN) after the first real point, in date order. A run between
 * two real points is a gap; one that lasts to the end of the series is `trailing`. Leading nulls are not
 * gaps: they are "data starts". */
export function gapRuns(points: { day: string; value: number | null | undefined }[]): GapRun[] {
  const real = (v: number | null | undefined) => v != null && !Number.isNaN(v);
  const first = points.findIndex((p) => real(p.value));
  if (first < 0) return [];
  const runs: GapRun[] = [];
  let start = -1;
  const close = (end: number, trailing: boolean) =>
    runs.push({
      from: points[start]!.day,
      to: points[end]!.day,
      count: end - start + 1,
      index: start,
      trailing,
    });
  for (let i = first; i < points.length; i++) {
    if (!real(points[i]!.value)) {
      if (start < 0) start = i;
    } else if (start >= 0) {
      close(i - 1, false);
      start = -1;
    }
  }
  if (start >= 0) close(points.length - 1, true);
  return runs;
}

/** Captions for a series' gaps, e.g. "no weigh-in 15. Jul – 5. Oct" (the axis date format) or, for a run that
 * lasts to the end, "no weigh-in since 5. Oct". `reason` may depend on the run. With more than `max` gaps the
 * longest `max` are listed (in date order) and "+N more" follows. */
export function gapLabels(
  points: { day: string; value: number | null | undefined }[],
  reason: string | ((run: GapRun) => string),
  max = 3,
): string[] {
  const runs = gapRuns(points);
  const shown = runs
    .map((r, i) => ({ r, i }))
    .sort((a, b) => b.r.count - a.r.count || a.i - b.i)
    .slice(0, max)
    .sort((a, b) => a.i - b.i)
    .map(({ r }) => {
      const why = typeof reason === "string" ? reason : reason(r);
      if (r.trailing) return `${why} since ${day(r.from)}`;
      return r.from === r.to ? `${why} ${day(r.from)}` : `${why} ${day(r.from)} – ${day(r.to)}`;
    });
  if (runs.length > max) shown.push(`+${runs.length - max} more`);
  return shown;
}

/** "data starts 7. Jun 2025" for the first real point (shown when a long range was cut to it). */
export function dataStartsLabel(d: string): string {
  return `data starts ${dayYear(d.slice(0, 10))}`;
}

/** One row for every calendar day from the first row to the last: a day the API left out (no row) becomes a
 * row made by `blank(day)` (all values null), so the category axis shows the break instead of putting the
 * days either side of it next to each other. Rows are sorted by day. */
export function fillDays<T extends { day: string }>(rows: T[], blank: (day: string) => T): T[] {
  if (rows.length < 2) return rows;
  const sorted = [...rows].sort((a, b) => a.day.localeCompare(b.day));
  const byDay = new Map(sorted.map((r) => [r.day.slice(0, 10), r]));
  const out: T[] = [];
  const last = sorted[sorted.length - 1]!.day.slice(0, 10);
  for (let d = sorted[0]!.day.slice(0, 10); d <= last; d = isoDay(addDays(parseISO(d), 1))) {
    out.push(byDay.get(d) ?? blank(d));
  }
  return out;
}
